import React, { useState, useEffect, useCallback } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { AdminLoginPage, AdminDashboard, AdminUsersPage, AdminSettingsPage } from "./components/admin/AdminPortal";
import { OutreachDashboard, OutreachLeadsPage, OutreachTemplatesPage, OutreachCampaignsPage, CampaignDetailPage, OutreachReferralsPage, OutreachQuickSendPage, OutreachAutomationPage, OutreachSettingsPage, OutreachPipelinePage, OutreachSectorPresetsPage, OutreachEngineSettingsPage, OutreachListsPage } from "./components/admin/OutreachModule";
import { ReachFoldersPage, ReachFolderDetailPage, ReachSettingsPage, ReachTemplatesPage, ReachSMTPPage, ReachReferralsPage, ReachAnalyticsPage, OutreachLegacyRedirect } from "./components/admin/ReachModule";
import AuthContext, { useAuth } from "./context/AuthContext";

/**
 * Resets window scroll position to the top on every route change so users
 * always land at the top of a new page (Dashboard → Invoices, etc.) instead
 * of inheriting the previous page's scroll offset.
 */
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
  }, [pathname]);
  return null;
};
import { CreateSubscriptionModal, ClientSubscriptionsPage, PublicSubscriptionPage, StripeSubscriptionPayment, UpdateCardPage } from "./components/subscriptions";
import { AuthScreen, ForgotPasswordPage, ResetPasswordPage, VerifyEmailPage } from "./components/auth/AuthComponents";
import SettingsPage from "./components/settings/SettingsPage";
import PrivacyPolicyPage from "./components/pages/PrivacyPolicyPage";
import TermsOfServicePage from "./components/pages/TermsOfServicePage";
import HelpPage from "./components/pages/HelpPage";
import FeaturesPage from "./components/pages/FeaturesPage";
import Dashboard from "./components/dashboard/Dashboard";
import CheckoutPage from "./components/checkout/CheckoutPage";
import ContractsPage from "./components/contracts/ContractsPage";
import ClientsPage from "./components/clients/ClientsPage";
import ProvidersPage from "./components/providers/ProvidersPage";
import { SubscriptionsPage, SubscriptionSuccess, SubscriptionCancel, SubscriptionGatedPage } from "./components/pricing/SubscriptionsPage";
import { VerifySignaturePage, VerifySubscriptionSignaturePage, VerifyPaymentSignaturePage, TestModalPage } from "./components/verification/VerificationPages";
import { EmbedBusiness, EmbedAllProjects, EmbeddableCheckout } from "./components/embed/EmbedComponents";
import { QuotesPage, QuoteViewPage } from "./components/quotes/QuotesComponents";
import { InvoicesPage } from "./components/invoices/InvoicesComponents";
import { VATVaultPage } from "./components/vat/VATComponents";
import { ProjectDetail, ProjectsPage } from "./components/projects/ProjectComponents";
import { Toaster } from "./components/ui/toaster";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = sessionStorage.getItem('token');
      
      if (storedToken) {
        setToken(storedToken);
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data);
        } catch (error) {
          sessionStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      
      const { access_token, user: userData } = response.data;
      
      if (!access_token || !userData) {
        throw new Error('Invalid response format: missing token or user data');
      }
      
      sessionStorage.removeItem('token');
      
      try {
        sessionStorage.setItem('token', access_token);
        
        const storedToken = sessionStorage.getItem('token');
        if (storedToken !== access_token) {
          throw new Error('Token storage failed - sessionStorage might be disabled');
        }
      } catch (storageError) {
        throw new Error('Unable to store authentication token.');
      }
      
      setToken(access_token);
      setUser(userData);
      
      await new Promise(resolve => setTimeout(resolve, 200));
      
      setLoading(false);
      setLoading(true);
      setLoading(false);
      
      return userData;
    } catch (error) {
      console.error('Login function error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      throw error;
    }
  }, []);

  const register = async (payload) => {
    // Registration no longer auto-logs in — user must verify email first
    const response = await axios.post(`${API}/auth/register`, payload);
    return response.data;
  };

  const logout = () => {
    sessionStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const MainRoutes = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (location.pathname.includes('//')) {
      const fixedPath = location.pathname.replace(/\/+/g, '/');
      navigate(fixedPath + location.search, { replace: true });
      return;
    }
  }, [location.pathname, location.search, navigate]);
  
  if (location.pathname.includes('//')) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e] mx-auto"></div>
          <p className="mt-4 text-gray-600 font-body">Redirecting...</p>
        </div>
      </div>
    );
  }
  
  const normalizedPath = location.pathname.replace(/\/+/g, '/');
  const isPublicRoute = normalizedPath.startsWith('/checkout/') || 
                       normalizedPath.startsWith('/quote/') ||
                       normalizedPath.startsWith('/embed/') || 
                       normalizedPath.startsWith('/embed-all/') || 
                       normalizedPath.startsWith('/embed-business/');
  
  if (loading && !isPublicRoute) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e] mx-auto"></div>
          <p className="mt-4 text-gray-600 font-body">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* PUBLIC ROUTES - No auth required */}
        <Route path="/login" element={user ? <Navigate to="/" /> : <AuthScreen />} />
        <Route path="/forgot-password" element={user ? <Navigate to="/" /> : <ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/checkout/:slug" element={<CheckoutPage />} />
        <Route path="/quote/:slug" element={<QuoteViewPage />} />
        <Route path="/embed/:slug" element={<EmbeddableCheckout />} />
        <Route path="/embed-all/:userIdentifier" element={<EmbedAllProjects />} />
        <Route path="/embed-business/:businessId" element={<EmbedBusiness />} />
        <Route path="/verify-signature/:token" element={<VerifySignaturePage />} />
        <Route path="/verify-subscription-signature/:token" element={<VerifySubscriptionSignaturePage />} />
        <Route path="/verify-payment-signature/:token" element={<VerifyPaymentSignaturePage />} />
        <Route path="/subscription/:slug" element={<PublicSubscriptionPage />} />
        <Route path="/subscription/:slug/update-card" element={<UpdateCardPage />} />
        <Route path="/test-modal" element={<TestModalPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        
        {/* ADMIN PORTAL ROUTES */}
        <Route path="/admin" element={<AdminLoginPage />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="/admin/settings" element={<AdminSettingsPage />} />
        
        {/* OUTREACH MODULE ROUTES (Admin Only) */}
        {/* Paify Reach (new simplified) */}
        <Route path="/admin/reach" element={<ReachFoldersPage />} />
        <Route path="/admin/reach/folders" element={<ReachFoldersPage />} />
        <Route path="/admin/reach/folders/:folder" element={<ReachFolderDetailPage />} />
        <Route path="/admin/reach/templates" element={<ReachTemplatesPage />} />
        <Route path="/admin/reach/smtp" element={<ReachSMTPPage />} />
        <Route path="/admin/reach/referrals" element={<ReachReferralsPage />} />
        <Route path="/admin/reach/analytics" element={<ReachAnalyticsPage />} />
        <Route path="/admin/reach/settings" element={<ReachSettingsPage />} />
        {/* Legacy outreach root → redirect into Reach */}
        <Route path="/admin/outreach" element={<OutreachLegacyRedirect />} />
        <Route path="/admin/outreach/lists" element={<OutreachListsPage />} />
        <Route path="/admin/outreach/leads" element={<OutreachLeadsPage />} />
        <Route path="/admin/outreach/templates" element={<OutreachTemplatesPage />} />
        <Route path="/admin/outreach/sector-presets" element={<OutreachSectorPresetsPage />} />
        <Route path="/admin/outreach/engine-settings" element={<OutreachEngineSettingsPage />} />
        <Route path="/admin/outreach/campaigns" element={<OutreachCampaignsPage />} />
        <Route path="/admin/outreach/campaigns/:campaignId" element={<CampaignDetailPage />} />
        <Route path="/admin/outreach/referrals" element={<OutreachReferralsPage />} />
        <Route path="/admin/outreach/quick-send" element={<OutreachQuickSendPage />} />
        <Route path="/admin/outreach/automation" element={<OutreachAutomationPage />} />
        <Route path="/admin/outreach/settings" element={<OutreachSettingsPage />} />
        <Route path="/admin/outreach/pipeline" element={<OutreachPipelinePage />} />
        
        {/* PROTECTED ROUTES */}
        <Route path="/projects/:projectId" element={user ? <ProjectDetail /> : <Navigate to="/" />} />
        <Route path="/projects" element={user ? <ProjectsPage /> : <Navigate to="/" />} />
        <Route path="/quotes" element={user ? <SubscriptionGatedPage component={QuotesPage} requiredPlan="paidd_plus" featureName="Quotes" /> : <Navigate to="/" />} />
        <Route path="/invoices" element={user ? <SubscriptionGatedPage component={InvoicesPage} requiredPlan="paidd_plus" featureName="Invoices" /> : <Navigate to="/" />} />
        <Route path="/vat-vault" element={user ? <SubscriptionGatedPage component={VATVaultPage} requiredPlan="paidd_plus" featureName="VAT Vault" /> : <Navigate to="/" />} />
        <Route path="/contracts" element={user ? <ContractsPage /> : <Navigate to="/" />} />
        <Route path="/clients" element={user ? <ClientsPage /> : <Navigate to="/" />} />
        <Route path="/subscriptions-manage" element={user ? <ClientSubscriptionsPage /> : <Navigate to="/" />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/subscriptions" element={<SubscriptionsPage />} />
        <Route path="/subscription/success" element={<SubscriptionSuccess />} />
        <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
        <Route path="/providers" element={user ? <ProvidersPage /> : <Navigate to="/" />} />
        <Route path="/settings" element={user ? <SettingsPage /> : <Navigate to="/" />} />
        <Route path="/help" element={user ? <HelpPage /> : <Navigate to="/" />} />
        
        {/* HOME */}
        <Route path="/" element={loading ? (
          <div className="min-h-screen bg-white flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e] mx-auto"></div>
              <p className="mt-4 text-gray-600 font-body">Loading...</p>
            </div>
          </div>
        ) : (user ? <Dashboard key={`user-${user.id || 'default'}-${Date.now()}`} /> : <AuthScreen key={`auth-${Date.now()}`} />)} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      <Toaster />
    </>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <MainRoutes key="main-routes" />
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
};

export default App;
